import React from "react";
import { initials } from "../../lib/format";
import { getViewerRole, subscribeViewer } from "../../lib/viewerContext";
import { getMemberStatus, subscribeMembers } from "../../lib/membersContext";

// AVATAR — displays a photo (data URL or http) OR initials fallback.
// Works for the standard .avatar (34px), .sidebar-avatar (32px), and
// .profile-avatar-lg (72px) classes used throughout the app.
//
// Phase-1 glossy ring system: applies one of the .ring-* utility classes
// defined in tokens.css based on the member's status. The ring is only
// rendered when the viewer's role is Super Admin, Limited Admin, or
// Pastor. Team Members see no rings on any avatar (including their own).
//
// Resolution priority: explicit `status` prop > `memberId` lookup >
// `name` lookup via the shared membersContext singleton.

// Member status → ring utility class in tokens.css.
const RING_CLASS = {
  "Active":       "ring-active",
  "New Member":   "ring-new",
  "Visitor":      "ring-visitor",
  "Guest":        "ring-guest",
  "Inactive":     "ring-inactive",
  "Left Church":  "ring-left",
  "Deceased":     "ring-deceased",
};

// Legacy neon palette kept for callers of the exported helper
// `STATUS_RING_COLORS` (e.g. FollowUp tree renderer using its own <img>).
// New code should prefer the CSS classes above.
const RING_COLORS = {
  "Active":       "#16a34a",
  "New Member":   "#2563eb",
  "Visitor":      "#d97706",
  "Guest":        "#7c3aed",
  "Inactive":     "#dc2626",
  "Deceased":     "#9ca3af",
  "Left Church":  "#1f2937",
};

const PRIVILEGED = new Set(["superadmin", "limited_admin", "pastor"]);

export function Avatar({
  name, photo, className = "avatar", style = {}, "data-testid": testId,
  status, memberId, roleOverride, noRing = false,
}) {
  const [viewerRole, setViewerRole] = React.useState(() => roleOverride || getViewerRole());
  React.useEffect(() => {
    if (roleOverride) return () => {};
    return subscribeViewer((u) => setViewerRole(u?.role || null));
  }, [roleOverride]);

  // Re-render whenever the shared members list changes so rings stay
  // accurate without prop-drilling status updates through every screen.
  const [, forceTick] = React.useState(0);
  React.useEffect(() => {
    if (status) return () => {}; // explicit status prop — no subscription needed
    return subscribeMembers(() => forceTick(t => t + 1));
  }, [status]);

  const effectiveRole = roleOverride || viewerRole;
  const resolvedStatus = status || (PRIVILEGED.has(effectiveRole) ? getMemberStatus({ memberId, name }) : null);
  // When Avatar is nested inside a <StatusRing> the parent already owns the
  // ring — Avatar must NOT self-apply a second one (would render as two nested
  // 2px borders). Callers wrapping in StatusRing pass `noRing`.
  const ringClass = !noRing && resolvedStatus && PRIVILEGED.has(effectiveRole)
    ? RING_CLASS[resolvedStatus]
    : null;

  const hasPhoto = typeof photo === "string" && photo.trim().length > 0;
  const merged = {
    ...style,
    ...(hasPhoto ? {
      backgroundImage: `url(${photo})`,
      backgroundSize: "cover",
      backgroundPosition: "center",
      backgroundRepeat: "no-repeat",
      color: "transparent",
    } : {}),
  };
  const combinedClassName = ringClass ? `${className} ${ringClass}` : className;
  return (
    <div
      className={combinedClassName}
      style={merged}
      data-testid={testId}
      data-status={resolvedStatus || undefined}
      data-ring={ringClass ? "on" : undefined}
    >
      {hasPhoto ? "" : initials(name)}
    </div>
  );
}

// Legacy exports — kept for callers that render their own <img>/element
// and need the raw hex colour (e.g. Follow-Up tree canvas node cards).
// New code should apply the `ring-*` classes from tokens.css instead.
export const STATUS_RING_COLORS = RING_COLORS;

// Compute an inline ring style — returns null for non-privileged viewers.
// Now maps to the Phase-1 palette so the tree canvas matches the shared
// Avatar rings colour-for-colour.
export function computeStatusRing(statusOrOpts, viewerRoleOverride) {
  const role = viewerRoleOverride || getViewerRole();
  if (!PRIVILEGED.has(role)) return null;
  const opts = typeof statusOrOpts === "string" ? { status: statusOrOpts } : (statusOrOpts || {});
  const resolved = opts.status || getMemberStatus({ memberId: opts.memberId, name: opts.name });
  if (!resolved || !RING_COLORS[resolved]) return null;
  const color = RING_COLORS[resolved];
  return {
    border: `3px solid ${color}`,
    boxShadow: `0 0 0 1.5px ${color}33, inset 0 1px 2px rgba(255,255,255,0.18), 0 2px 10px ${color}4d`,
  };
}

// Expose the class map so other renderers can add the same class
// (and thus inherit the glossy ::after overlay).
export const STATUS_RING_CLASSES = RING_CLASS;
